import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const STEPS = [
  { id: 1, label: 'Workspace',  icon: '🏢' },
  { id: 2, label: 'Admin',      icon: '👤' },
  { id: 3, label: 'Ready',      icon: '🚀' },
];

export default function Setup({ onLogin }) {
  const navigate = useNavigate();
  const [step, setStep]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const [workspace, setWorkspace] = useState({ name: '', description: '' });
  const [admin, setAdmin]         = useState({ email: '', password: '', confirmPassword: '' });

  /* ─── helpers ─── */
  const next = () => { setError(''); setStep(s => s + 1); };
  const back = () => { setError(''); setStep(s => s - 1); };

  const handleWorkspaceNext = e => {
    e.preventDefault();
    if (!workspace.name.trim()) { setError('Workspace name is required'); return; }
    next();
  };

  const handleAdminSubmit = async e => {
    e.preventDefault();
    setError('');
    if (admin.password !== admin.confirmPassword) { setError('Passwords do not match'); return; }
    if (admin.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/setup`, {
        email: admin.email,
        password: admin.password,
        workspaceName: workspace.name,
      });
      const { token, user } = res.data.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('workspaceName', workspace.name);
      if (onLogin) onLogin(user);
      next();
    } catch (err) {
      setError(err.response?.data?.message || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goToDashboard = () => navigate('/home');

  /* ─── shared error block ─── */
  const ErrorAlert = () => error ? (
    <div className="alert alert-danger d-flex align-items-center gap-2 py-2" style={{ fontSize: 14 }}>
      <span>⚠️</span> {error}
    </div>
  ) : null;

  /* ─── progress bar ─── */
  const Progress = () => (
    <div className="d-flex align-items-center justify-content-center gap-0 mb-5">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <div className="d-flex flex-column align-items-center" style={{ minWidth: 80 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: step > s.id ? '#10b981' : step === s.id ? '#6366f1' : '#e5e7eb',
              color: step >= s.id ? '#fff' : '#9ca3af',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: step > s.id ? 18 : 16, fontWeight: 700,
              transition: 'all .3s',
              boxShadow: step === s.id ? '0 0 0 4px rgba(99,102,241,.2)' : 'none',
            }}>
              {step > s.id ? '✓' : s.icon}
            </div>
            <span style={{
              fontSize: 11, fontWeight: step === s.id ? 700 : 400,
              color: step === s.id ? '#6366f1' : step > s.id ? '#10b981' : '#9ca3af',
              marginTop: 4,
            }}>{s.label}</span>
          </div>
          {i < STEPS.length - 1 && (
            <div style={{
              flex: 1, height: 2, marginBottom: 18,
              background: step > s.id ? '#10b981' : '#e5e7eb',
              transition: 'background .3s',
            }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '2rem',
    }}>
      <div style={{ width: '100%', maxWidth: 520 }}>
        {/* Logo */}
        <div className="text-center mb-4">
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 56, height: 56, borderRadius: 14,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            fontSize: 28, fontWeight: 900, color: '#fff',
            marginBottom: 12, boxShadow: '0 8px 24px rgba(99,102,241,.4)',
          }}>Z</div>
          <h2 style={{ color: '#fff', fontWeight: 800, margin: 0 }}>Welcome to ZipQA</h2>
          <p style={{ color: '#94a3b8', marginTop: 4, fontSize: 14 }}>
            Let's get your workspace ready in a few quick steps.
          </p>
        </div>

        {/* Card */}
        <div style={{
          background: '#fff', borderRadius: 20,
          padding: '2.5rem', boxShadow: '0 24px 64px rgba(0,0,0,.3)',
        }}>
          <Progress />

          {/* ── Step 1: Workspace ── */}
          {step === 1 && (
            <form onSubmit={handleWorkspaceNext}>
              <h5 style={{ fontWeight: 700, marginBottom: 4 }}>Name your workspace</h5>
              <p className="text-muted mb-4" style={{ fontSize: 13 }}>
                This is the name your team will see — e.g. "Acme QA" or "Internal Testing".
              </p>
              <ErrorAlert />
              <div className="mb-3">
                <label className="form-label fw-semibold">Workspace Name <span className="text-danger">*</span></label>
                <input
                  className="form-control form-control-lg"
                  placeholder="e.g. Acme QA Team"
                  value={workspace.name}
                  onChange={e => setWorkspace(p => ({ ...p, name: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="mb-4">
                <label className="form-label fw-semibold">Description <span className="text-muted fw-normal">(optional)</span></label>
                <input
                  className="form-control"
                  placeholder="What does this workspace manage?"
                  value={workspace.description}
                  onChange={e => setWorkspace(p => ({ ...p, description: e.target.value }))}
                />
              </div>
              <button type="submit" className="btn w-100 fw-semibold" style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', padding: '12px', fontSize: 15, borderRadius: 10,
              }}>
                Continue →
              </button>
            </form>
          )}

          {/* ── Step 2: Admin Account ── */}
          {step === 2 && (
            <form onSubmit={handleAdminSubmit}>
              <h5 style={{ fontWeight: 700, marginBottom: 4 }}>Create the admin account</h5>
              <p className="text-muted mb-4" style={{ fontSize: 13 }}>
                This account will have full control over <strong>{workspace.name}</strong> — users, projects, and global settings.
              </p>
              <ErrorAlert />
              <div className="mb-3">
                <label className="form-label fw-semibold">Email Address <span className="text-danger">*</span></label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="admin@yourcompany.com"
                  value={admin.email}
                  onChange={e => setAdmin(p => ({ ...p, email: e.target.value }))}
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Password <span className="text-danger">*</span></label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Min. 6 characters"
                  value={admin.password}
                  onChange={e => setAdmin(p => ({ ...p, password: e.target.value }))}
                  required
                  minLength={6}
                />
              </div>
              <div className="mb-4">
                <label className="form-label fw-semibold">Confirm Password <span className="text-danger">*</span></label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Repeat password"
                  value={admin.confirmPassword}
                  onChange={e => setAdmin(p => ({ ...p, confirmPassword: e.target.value }))}
                  required
                />
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary fw-semibold flex-shrink-0" onClick={back}>← Back</button>
                <button type="submit" className="btn flex-grow-1 fw-semibold" disabled={loading} style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', borderRadius: 10, fontSize: 15,
                }}>
                  {loading
                    ? <><span className="spinner-border spinner-border-sm me-2" />Creating…</>
                    : '🔐 Create Admin & Finish'}
                </button>
              </div>
            </form>
          )}

          {/* ── Step 3: Done ── */}
          {step === 3 && (
            <div className="text-center py-2">
              <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
              <h5 style={{ fontWeight: 800, marginBottom: 8 }}>You're all set!</h5>
              <p className="text-muted mb-1" style={{ fontSize: 14 }}>
                <strong>{workspace.name}</strong> is ready. You're signed in as <strong>{admin.username}</strong> with Admin access.
              </p>
              <p className="text-muted mb-4" style={{ fontSize: 13 }}>
                From Settings → User Management you can add team members and assign them roles.
              </p>
              <div className="card border-0 text-start mb-4" style={{ background: '#f8fafc', borderRadius: 12 }}>
                <div className="card-body py-3">
                  <p className="mb-2 fw-semibold" style={{ fontSize: 13 }}>Quick next steps:</p>
                  <ul className="mb-0" style={{ fontSize: 13, paddingLeft: '1.2rem', color: '#475569', lineHeight: 2 }}>
                    <li>Create your first <strong>Project</strong></li>
                    <li>Invite team members via <strong>Settings → User Management</strong></li>
                    <li>Organise people into <strong>Teams</strong> for easy project assignment</li>
                    <li>Connect <strong>JIRA</strong> or <strong>Jenkins</strong> under Settings</li>
                  </ul>
                </div>
              </div>
              <button
                className="btn w-100 fw-semibold"
                style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', padding: '12px', fontSize: 15, borderRadius: 10 }}
                onClick={goToDashboard}
              >
                🚀 Go to Dashboard
              </button>
            </div>
          )}
        </div>

        <p className="text-center mt-3" style={{ color: '#475569', fontSize: 12 }}>
          Already have an account? <a href="/login" style={{ color: '#818cf8' }}>Sign in</a>
        </p>
      </div>
    </div>
  );
}
